/* eslint-disable camelcase */
import { Box, Typography, Divider, Button, Grid } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { COLORS } from '@scope/scope-one-ui/theme';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import React, { FC, useState, useEffect } from 'react';
import { ErrorBoundary } from 'shared/errors';
import { Loader } from 'ui';
import { useReportPage } from 'shared/hooks';
import HorizontalBarChart from 'ui/Charts/HorizontalBarChart/HorizontalBarChart';
import { LegendColorChip } from 'ui/ColorChips/LegendColorChip';
import { IndustryScore, RegionScore } from '../types';

const useStyles = makeStyles((theme) => ({
  divider: {
    alignSelf: 'stretch',
    height: 'auto',
    margin: theme.spacing(0, 2),
  },
  companies: {
    marginTop: theme.spacing(6),
  },
  bold: {
    fontWeight: 900,
  },
  chipSpacing: {
    marginRight: theme.spacing(1),
  },
  title: {
    paddingLeft: theme.spacing(2),
    fontSize: useReportPage ? 12 : 16,
  },
  graphLabel: {
    color: COLORS.primary.main,
  },
  container: {
    textTransform: 'none',
    fontWeight: 400,
    color: theme.palette.grey[500],
    borderColor: theme.palette.grey[500],
    borderRadius: 16,
    height: 32,
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    marginRight: theme.spacing(1),
  },
  colorActive: {
    backgroundColor: '#013B6C',
    color: 'white',
    '&:hover': {
      backgroundColor: '#013B6C',
      color: 'white',
    },
  },
}));

type ESGLendingImpact = {
  industriesAbsolute: IndustryScore[];
  industriesRelative: IndustryScore[];
  countriesAbsolute: RegionScore[];
  countriesRelative: RegionScore[];
  loading?: boolean;
};

const ESGLendingImpact: FC<ESGLendingImpact> = (props) => {
  const { loading = false } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const [environmentalCostCountry, setEnvironmentalCostCountry] = useState<Array<number>>([]);
  const [socialCostCountry, setSocialCostCountry] = useState<Array<number>>([]);
  const [governanceCostCountry, setGovernanceCostCountry] = useState<Array<number>>([]);
  const [isAbsolute, setIsAbsolute] = useState(true);

  const suffix: string = isAbsolute ? 'Absolute' : 'Relative';
  const countries = props[`countries${suffix}`] as RegionScore[];
  const industries = props[`industries${suffix}`] as IndustryScore[];

  const mapCountryDatasets = () => {
    const environmentalCost = countries.map((el) => el.eScore);
    const socialCost = countries.map((el) => el.sScore);
    const governanceCost = countries.map((el) => el.gScore);

    setEnvironmentalCostCountry(environmentalCost);
    setSocialCostCountry(socialCost);
    setGovernanceCostCountry(governanceCost);
  };

  const [environmentalCostIndustry, setEnvironmentalCostIndustry] = useState<Array<number>>([]);
  const [socialCostIndustry, setSocialCostIndustry] = useState<Array<number>>([]);
  const [governanceCostIndustry, setGovernanceCostIndustry] = useState<Array<number>>([]);

  const mapIndustryDatasets = () => {
    const environmentalCost = industries.map((el) => el.eScore);
    const socialCost = industries.map((el) => el.sScore);
    const governanceCost = industries.map((el) => el.gScore);

    setEnvironmentalCostIndustry(environmentalCost);
    setSocialCostIndustry(socialCost);
    setGovernanceCostIndustry(governanceCost);
  };

  const showChart = () => {
    setIsAbsolute(!isAbsolute);
  };

  useEffect(() => {
    if (countries.length) {
      mapCountryDatasets();
    }

    if (industries.length) {
      mapIndustryDatasets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries, industries, isAbsolute]);

  const countryOptions = {
    indexAxis: 'y',
    animation: false,
    elements: {
      bar: {
        borderWidth: 1,
      },
    },
    datasets: {
      bar: {
        barThickness: 20,
      },
    },
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        stacked: true,
      },
      x: {
        stacked: true,
        ticks: { callback: (value: number) => `${value}` },
      },
    },
  };

  const indostryOptions = {
    ...countryOptions,
    scales: {
      y: {
        stacked: true,
      },
      x: {
        stacked: true,
        ticks: { callback: (value: number) => `${value}` },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  const countryData = {
    labels: countries.map((el) => el.country),
    datasets: [
      {
        label: 'Environment',
        data: environmentalCostCountry,
        backgroundColor: ['#013B6C'],
        borderColor: '#013B6C',
      },
      {
        label: 'Social',
        data: socialCostCountry,
        backgroundColor: ['#598CB8'],
        borderColor: '#598CB8',
      },
      {
        label: 'Governance',
        data: governanceCostCountry,
        backgroundColor: ['#AECCE5'],
        borderColor: '#AECCE5',
      },
    ],
  };

  const industryData = {
    labels: industries.map((el) => el.industry),
    datasets: [
      {
        label: 'Environment',
        data: environmentalCostIndustry,
        backgroundColor: ['#013B6C'],
        borderColor: '#013B6C',
      },
      {
        label: 'Social',
        data: socialCostIndustry,
        backgroundColor: ['#598CB8'],
        borderColor: '#598CB8',
      },
      {
        label: 'Governance',
        data: governanceCostIndustry,
        backgroundColor: ['#AECCE5'],
        borderColor: '#AECCE5',
      },
    ],
  };

  const graphLabel = () => (
    <Box p={1} display="flex" justifyContent="flex-end">
      {isAbsolute ? (
        <Typography variant="caption" className={classes.graphLabel}>
          {t('Absolute Impact (EUR)')}
        </Typography>
      ) : (
        <Typography variant="caption" className={classes.graphLabel}>
          {t('Relative Impact (EUR/EUR of Revenues)')}
        </Typography>
      )}
    </Box>
  );

  return (
    <>
      <Box p={2} flexDirection="row">
        <Grid container>
          <Grid item xs={8}>
            <Box display="flex" alignItems="baseline">
              <Typography variant="h6">{t('ESG.NEXT_IMPACT_BY_REG_IND')}</Typography>
            </Box>
          </Grid>
          {!useReportPage ? (
            <Grid container item xs={4} justifyContent="flex-end">
              <Button
                variant={isAbsolute ? 'contained' : 'outlined'}
                className={clsx(classes.container, isAbsolute && classes.colorActive)}
                size="small"
                onClick={showChart}
              >
                Absolute
              </Button>
              <Button
                variant={isAbsolute ? 'outlined' : 'contained'}
                className={clsx(classes.container, !isAbsolute && classes.colorActive)}
                size="small"
                onClick={showChart}
              >
                Relative
              </Button>
            </Grid>
          ) : null}
        </Grid>
        <Box py={2}>
          <Divider />
        </Box>
      </Box>
      <Box p={2} pb={5}>
        {loading ? (
          <Loader center />
        ) : (
          <Box display="flex" flexDirection="column">
            <Box flexGrow={1} padding={2}>
              <Typography variant="subtitle1" className={clsx(classes.title, classes.bold)}>
                {t('ESG.IMPACT_BY_COUNTRY')}
              </Typography>

              <Box display="flex" justifyContent="center" width={1} pl={9}>
                <ErrorBoundary>
                  <HorizontalBarChart
                    data={countryData}
                    options={countryOptions}
                    height={useReportPage ? 150 : 100}
                  />
                </ErrorBoundary>
              </Box>
              {graphLabel()}
            </Box>

            <Box flexGrow={1} padding={2}>
              <Typography variant="subtitle1" className={clsx(classes.title, classes.bold)}>
                {t('ESG.IMPACT_BY_INDUSTRY')}
              </Typography>
              <Box display="flex" justifyContent="center" width={1}>
                <ErrorBoundary>
                  <HorizontalBarChart
                    data={industryData}
                    options={indostryOptions}
                    height={useReportPage ? 150 : 100}
                  />
                </ErrorBoundary>
              </Box>
              {graphLabel()}
              <Box display="flex" alignItems="center" pt={4}>
                <Box display="flex" alignItems="center" pr={4}>
                  <LegendColorChip className={classes.chipSpacing} chipColor="#013B6C" />
                  <Typography variant="body1" className={classes.bold}>
                    Environment
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" pr={4}>
                  <LegendColorChip className={classes.chipSpacing} chipColor="#598CB8" />
                  <Typography variant="body1" className={classes.bold}>
                    Social
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <LegendColorChip className={classes.chipSpacing} chipColor="#AECCE5" />
                  <Typography variant="body1" className={classes.bold}>
                    Governance
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default ESGLendingImpact;
